/**
 * Focus — the owner's own goals, week plan, pomodoro and team commits.
 *
 * Wire shapes mirror `system/services/focus_plan.py :: serialize_block /
 * serialize_focus_goal / load_week`, `focus_timer.py :: timer_state`,
 * `focus_changes.py :: pending_summary / serialize_change / serialize_commit`
 * and `focus_calendar.py :: serialize_background` — change them together.
 * Errors arrive as `detail = {error: code, message}`; read them through
 * `apiErrorDetail` / `apiErrorCode`.
 */
import { api } from "./client";

export type FocusAudience = "me" | "ai" | "all";
export type FocusGoalStatus = "active" | "paused" | "achieved" | "retired";
export type BlockProtection = "soft" | "protected";
export type BlockStatus = "planned" | "removed" | "removed_externally";
export type CalendarSyncState = "pending" | "synced" | "error" | "not_connected";
export type OutlookState = "synced" | "stale" | "not_connected" | "error" | "not_loaded";
export type TimerPhase = "idle" | "focus" | "short_break" | "long_break";

export interface SeatRef {
  id: string;
  slug: string | null;
  name: string | null;
  disabled?: boolean;
}

export interface WeekWindow {
  start_date: string;
  end_date: string;
  starts_at: string;
  ends_at: string;
  /** Server period label (`%Y-W%W`, not the ISO week) — the UI shows its own KW. */
  label: string;
  timezone: string;
  prev_start: string;
  next_start: string;
  days: string[];
}

/** Plan vs. actual for one goal in one week, in minutes. */
export interface GoalWeekStats {
  budget_minutes: number | null;
  planned_minutes: number;
  actual_minutes: number;
  completed_focus_sessions: number;
  planned_by_day: Record<string, number>;
  actual_by_day: Record<string, number>;
  remaining_budget_minutes: number | null;
}

export interface GoalDefinition {
  title: string;
  purpose: string;
  outcome: string;
  criteria: string[];
  priority: number;
  target_date: string | null;
  [key: string]: unknown;
}

export interface FocusGoal {
  id: string;
  title: string;
  audience: FocusAudience;
  assigned_agent: SeatRef | null;
  status: FocusGoalStatus;
  rank: number | null;
  weekly_minutes: number | null;
  next_step: string | null;
  plannable: boolean;
  definition: GoalDefinition | null;
  version: { id: string; number: number } | null;
  generation: number;
  has_newer_draft: boolean;
  week: (GoalWeekStats & { label: string }) | null;
  updated_at: string | null;
}

/** A goal row of the week payload (flat stats). */
export interface WeekGoal extends GoalWeekStats {
  id: string;
  title: string;
  audience: FocusAudience;
  status: FocusGoalStatus;
  rank: number | null;
  weekly_minutes: number | null;
  next_step: string | null;
  plannable: boolean;
}

export interface FocusBlock {
  id: string;
  goal_id: string;
  goal_title: string | null;
  goal_status: FocusGoalStatus | null;
  /** Europe/Berlin offset ISO. */
  starts_at: string;
  ends_at: string;
  minutes: number;
  protection: BlockProtection;
  status: BlockStatus;
  note: string | null;
  calendar: {
    state: CalendarSyncState;
    event_id: string | null;
    error: string | null;
    synced_at: string | null;
  };
  actual_minutes: number;
  revision: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface BlockWarning {
  kind: "outlook_overlap";
  event_id: string;
  subject: string | null;
}

export type CreatedBlock = FocusBlock & { warnings?: BlockWarning[] };

/** An Outlook event rendered as read-only background. */
export interface OutlookEvent {
  id: string;
  subject: string | null;
  starts_at: string | null;
  ends_at: string | null;
  all_day: boolean;
  /** Local date of an all-day event. */
  date: string | null;
  show_as: string | null;
  is_online_meeting: boolean;
  categories: string[] | null;
  /** A focus marker this account doesn't own (another plan's block). */
  foreign_focus: boolean;
}

export interface BlockRef {
  id: string;
  goal_id: string;
  goal_title: string | null;
  starts_at: string;
  ends_at: string;
  protection: BlockProtection;
}

export interface TimerSession {
  id: string;
  kind: Exclude<TimerPhase, "idle">;
  goal_id: string | null;
  goal_title: string | null;
  block_id: string | null;
  started_at: string;
  planned_end_at: string;
  note: string | null;
}

export interface TimerState {
  phase: TimerPhase;
  /** UTC `…Z`. */
  server_now: string;
  session: TimerSession | null;
  remaining_seconds: number | null;
  then: { kind: TimerPhase; starts_at: string; ends_at: string | null } | null;
  cycle: { completed_focus_today: number; since_long_break: number; long_break_every: number };
  settings: { focus_minutes: number; short_break_minutes: number; long_break_minutes: number };
  today: { focus_minutes: number; sessions: number };
  current_block: BlockRef | null;
  next_block: BlockRef | null;
  suggested_goal_id: string | null;
}

/** A timer state plus the server-minus-client clock offset measured with it. */
export type TimerSnapshot = TimerState & { clock_offset_ms: number };

export type ChangeKind =
  | "goal_created"
  | "goal_updated"
  | "goals_reranked"
  | "block_added"
  | "block_removed"
  | "block_restored"
  | "block_moved"
  | "block_updated";

export interface ChangeItem {
  kind: ChangeKind;
  entity_id: string | null;
  label: string;
  from: Record<string, unknown> | null;
  to: Record<string, unknown> | null;
  sources: string[];
  team_visible: boolean;
  already_announced: boolean;
}

export interface PendingChanges {
  count: number;
  affects_team: boolean;
  since: string | null;
  items: ChangeItem[];
  chief_of_staff: SeatRef | null;
}

export interface CommitRef {
  id: string;
  created_at: string | null;
  change_count: number;
  note: string | null;
  outcome: "informed" | "no_cos" | "ambiguous_cos";
  informed: SeatRef | null;
  seats_notified: number;
  lines: string[];
  job: {
    id: string;
    status: string;
    result: { summary_md?: string; [key: string]: unknown } | null;
    error: string | null;
  } | null;
}

export type CommitResult =
  | { committed: true; commit: CommitRef; lines: string[] }
  | { committed: false; reason: "nothing_to_commit"; pending: PendingChanges };

export interface GoalsPayload {
  goals: FocusGoal[];
  week: WeekWindow;
  pending_changes: PendingChanges;
}

export interface WeekPayload {
  week: WeekWindow;
  goals: WeekGoal[];
  blocks: FocusBlock[];
  outlook: {
    state: OutlookState;
    mailbox: string | null;
    events: OutlookEvent[];
    reconciled: Record<string, unknown>;
  };
  totals: { budget_minutes: number; planned_minutes: number; actual_minutes: number };
  timer: TimerState;
  pending_changes: PendingChanges;
  last_commit: CommitRef | null;
}

export interface CopyResult {
  created: FocusBlock[];
  skipped: { source_block_id: string; reason: string }[];
}

export interface GoalCreateBody {
  request_id: string;
  title: string;
  audience?: FocusAudience;
  assigned_agent_id?: string | null;
  status?: "active" | "paused";
  weekly_minutes?: number | null;
  next_step?: string | null;
}

/** Send only the keys that changed; `null` clears budget / next step / assignee. */
export interface GoalPatchBody {
  title?: string;
  status?: FocusGoalStatus;
  audience?: FocusAudience;
  assigned_agent_id?: string | null;
  weekly_minutes?: number | null;
  next_step?: string | null;
  remove_future_blocks?: boolean;
}

export interface BlockCreateBody {
  goal_id: string;
  starts_at: string;
  ends_at: string;
  protection?: BlockProtection;
  note?: string | null;
}

export interface BlockPatchBody {
  goal_id?: string;
  starts_at?: string;
  ends_at?: string;
  protection?: BlockProtection;
  note?: string | null;
  restore?: boolean;
  expected_revision?: number;
}

/**
 * Query keys. Everything under `["focus"]` is invalidated by one debounced
 * SSE frame (`FocusTimerChip`); the owner id lives outside that subtree — it
 * never changes, so a frame must not refetch it.
 */
export const FOCUS_KEYS = {
  all: ["focus"] as const,
  goals: (status: string) => ["focus", "goals", status] as const,
  week: (start: string) => ["focus", "week", start] as const,
  weeks: ["focus", "week"] as const,
  timer: ["focus", "timer"] as const,
  changes: ["focus", "changes"] as const,
  commits: ["focus", "commits"] as const,
  seats: ["focus", "seats"] as const,
  owner: ["focus-owner"] as const,
};

/**
 * Run a timer request and measure the clock offset with it: the server's
 * `server_now` is taken as the midpoint of the round trip, so the countdown
 * reads `planned_end_at − (Date.now() + offset)` instead of trusting a local
 * clock that may be minutes off.
 */
export async function withClockOffset(fn: () => Promise<TimerState>): Promise<TimerSnapshot> {
  const sent = Date.now();
  const state = await fn();
  const received = Date.now();
  const server = Date.parse(state.server_now);
  const offset = Number.isFinite(server) ? server - (sent + received) / 2 : 0;
  return { ...state, clock_offset_ms: offset };
}

const BASE = "/api/v1/focus";

export const focusApi = {
  owner: () => api.get<{ user: { id: string } }>("/api/v1/me"),

  goals: (status = "all", audience = "any") =>
    api.get<GoalsPayload>(`${BASE}/goals`, { status, audience }),
  createGoal: (body: GoalCreateBody) => api.post<FocusGoal>(`${BASE}/goals`, body),
  patchGoal: (id: string, body: GoalPatchBody) => api.patch<FocusGoal>(`${BASE}/goals/${id}`, body),
  reorder: (goalIds: string[]) =>
    api.post<{ goals: { id: string; rank: number }[]; team_order_changed: boolean }>(
      `${BASE}/goals/reorder`,
      { goal_ids: goalIds },
    ),
  seats: () => api.get<SeatRef[]>(`${BASE}/seats`),

  week: (start?: string, refresh = false) =>
    api.get<WeekPayload>(`${BASE}/week`, { start, refresh: refresh || undefined }),
  copyWeek: (fromStart: string, toStart: string) =>
    api.post<CopyResult>(`${BASE}/week/copy`, { from_start: fromStart, to_start: toStart }),

  createBlock: (body: BlockCreateBody) => api.post<CreatedBlock>(`${BASE}/blocks`, body),
  patchBlock: (id: string, body: BlockPatchBody) =>
    api.patch<FocusBlock>(`${BASE}/blocks/${id}`, body),
  deleteBlock: (id: string) => api.delete<void>(`${BASE}/blocks/${id}`),
  syncBlock: (id: string) => api.post<FocusBlock>(`${BASE}/blocks/${id}/sync`),

  timer: () => withClockOffset(() => api.get<TimerState>(`${BASE}/timer`)),
  start: (body: { goal_id?: string; block_id?: string; expected_session_id?: string }) =>
    withClockOffset(() => api.post<TimerState>(`${BASE}/timer/start`, body)),
  stop: (body: { mode?: "abort" | "skip"; expected_session_id?: string }) =>
    withClockOffset(() => api.post<TimerState>(`${BASE}/timer/stop`, body)),
  note: (note: string, sessionId?: string) =>
    withClockOffset(() =>
      api.post<TimerState>(`${BASE}/timer/note`, { note, session_id: sessionId }),
    ),

  changes: () => api.get<PendingChanges>(`${BASE}/changes`),
  commit: (requestId: string, note?: string) =>
    api.post<CommitResult>(`${BASE}/commit`, { request_id: requestId, note: note || undefined }),
  commits: (limit = 5) => api.get<CommitRef[]>(`${BASE}/commits`, { limit }),
};
